import { useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SyncBranchModal } from '@/components/SyncBranchModal'
import { ArrowLeft } from 'lucide-react'
import type { PageProps, StaffMember } from '@/types'

const COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6b7280',
]

interface BusinessOption {
  id: number
  name: string
}

interface EditProps extends PageProps {
  staff_member: StaffMember
  errors?: Record<string, string>
  is_admin?: boolean
  businesses?: BusinessOption[]
  assigned_business_ids?: number[]
}

export default function Edit({
  staff_member,
  errors = {},
  is_admin = false,
  businesses = [],
  assigned_business_ids = [],
  current_user,
  current_organization,
  current_business,
}: EditProps) {
  const orgSlug = current_organization?.slug || ''

  const [selectedBusinessIds, setSelectedBusinessIds] = useState<number[]>(assigned_business_ids)
  const [showSyncModal, setShowSyncModal] = useState(false)

  const { data, setData, processing } = useForm({
    role: staff_member.role,
    color: staff_member.color || COLORS[0],
    active: staff_member.active,
  })

  const submitForm = (syncAll = false) => {
    router.put(`/${orgSlug}/team/${staff_member.id}`, {
      staff_assignment: { ...data, business_ids: selectedBusinessIds, ...(syncAll ? { sync_all: true } : {}) }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if ((staff_member.sibling_count ?? 0) > 0) {
      setShowSyncModal(true)
      return
    }
    submitForm()
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/team`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al equipo
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            Editar miembro
          </h1>
          <p className="text-muted-foreground">{staff_member.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            {is_admin && (
              <div className="space-y-2">
                <Label htmlFor="role">Rol</Label>
                <select
                  id="role"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={data.role}
                  onChange={(e) => setData('role', e.target.value as 'staff' | 'admin')}
                >
                  <option value="staff">Personal - Solo ve sus citas</option>
                  <option value="admin">Administrador - Acceso completo</option>
                </select>
                {errors.role && (
                  <p className="text-sm text-destructive">{errors.role}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Color en calendario</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-8 w-8 rounded-full transition-all ${
                      data.color === color
                        ? 'ring-2 ring-ring ring-offset-2 ring-offset-background'
                        : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setData('color', color)}
                  />
                ))}
              </div>
            </div>

            {is_admin && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  className="h-4 w-4 rounded border-input"
                  checked={data.active}
                  onChange={(e) => setData('active', e.target.checked)}
                />
                <Label htmlFor="active" className="font-normal">
                  Miembro activo (puede recibir citas)
                </Label>
              </div>
            )}
          </div>

          {is_admin && businesses.length > 1 && (
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="space-y-2">
                <Label>Sucursales asignadas</Label>
                <p className="text-xs text-muted-foreground">
                  Selecciona en qué sucursales trabaja este miembro
                </p>
                <div className="space-y-2 mt-2">
                  {businesses.map((biz) => (
                    <label key={biz.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBusinessIds.includes(biz.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBusinessIds([...selectedBusinessIds, biz.id])
                          } else {
                            setSelectedBusinessIds(selectedBusinessIds.filter(id => id !== biz.id))
                          }
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">{biz.name}</span>
                    </label>
                  ))}
                </div>
                {selectedBusinessIds.length === 0 && (
                  <p className="text-sm text-destructive">Debe estar en al menos una sucursal</p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={processing || selectedBusinessIds.length === 0}>
              {processing ? 'Guardando...' : 'Guardar cambios'}
            </Button>
            <Link href={`/${orgSlug}/team`}>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>

        <SyncBranchModal
          open={showSyncModal}
          title="Actualizar en todas las sucursales"
          description={`Este miembro existe en ${staff_member.sibling_count ?? 0} sucursal(es) más. ¿Deseas aplicar los cambios en todas?`}
          confirmLabel="Actualizar en todas las sucursales"
          singleLabel="Solo en esta sucursal"
          onSyncAll={() => {
            setShowSyncModal(false)
            submitForm(true)
          }}
          onSingleOnly={() => {
            setShowSyncModal(false)
            submitForm(false)
          }}
          onCancel={() => setShowSyncModal(false)}
        />
      </div>
    </DashboardLayout>
  )
}
